import {
  DatePicker,
  Form,
  Input,
  InputProps,
  Space,
  Tooltip,
  Typography,
} from 'antd';
import { ReactNode, forwardRef, memo, useImperativeHandle } from 'react';
import { useNavigate } from 'react-router-dom';

import { Rule } from 'antd/es/form';
import { EditIcon, EyeIcon } from 'assets/svg';
import { ButtonAction } from 'components';
import TableComponent from 'components/Table/TableComponent';
import dayjs from 'dayjs';
import useQueryParam from 'hooks/useQueryPrams';
import { ColumnsTypeCustom } from 'interfaces/Table/ColumnType';
import { IModules } from 'pages/Plant/HOPlan/TableHOPlan';
import path from 'routes/Path';
import { HOFollowCustomerPagination } from 'utils/HOFollowCustomPagination';
import { DEFAULT_PAGE_SIZE } from 'utils/constants/constant';
import { formatCurrency, formatDate } from 'utils/constants/functions';
import { icons } from 'utils/constants/icons';
import { IData } from './HOFollowPlan';

interface IProps {
  data: IData;
  loading?: boolean;
  modules?: IModules[];
  setData?: any;
  pagination?: boolean;
  editIndex?: number;
  onCancel: () => void;
  setEditIndex: (data: any) => void;
  onRowChange: (record: any, newValue: any) => void;
}

interface IRenderTable {
  rules?: Rule[];
  message: string;
  inputProps: InputProps;
  component?: ReactNode;
}

// const unit = 1000000;
const { SaveTwoTone, CloseCircleTwoTone } = icons;
const { Text } = Typography;
const TableHOFollowPlan = memo(
  forwardRef(
    (
      {
        data,
        loading,
        editIndex,
        pagination = true,
        modules = [IModules.DETAIL],
        onCancel,
        setEditIndex,
        onRowChange,
      }: IProps,
      ref: any,
    ) => {
      const navigate = useNavigate(),
        [form] = Form.useForm();

      useImperativeHandle(ref, () => ({ getData }));

      const queryParam = useQueryParam(),
        page = parseInt(queryParam.get('page') + '') || 1,
        page_size =
          parseInt(queryParam.get('page_size') + '') || DEFAULT_PAGE_SIZE;

      // const renderValue = (value: any) => {
      //   const formattedValue = value;

      //   return formattedValue === '' || formattedValue === null
      //     ? '-'
      //     : formattedValue;
      // };

      const renderTable = ({ rules, component, inputProps }: IRenderTable) => {
        const { name, value } = inputProps,
          customRules = rules !== undefined ? rules : ([] as Rule[]);

        return (
          <Form.Item
            name={name}
            className="m-0"
            rules={customRules}
            initialValue={value}
          >
            {component ? (
              component
            ) : (
              <Input
                value={value}
                className="rounded-xl h-10"
                {...inputProps}
              />
            )}
          </Form.Item>
        );
      };
      const renderActionButton = (record: any, index: number) => {
        const isEdit = Number(record.year) >= new Date().getFullYear();
        const classes = isEdit
          ? 'cursor-pointer'
          : 'opacity-50 pointer-events-none';

        const handleClickEdit = () => {
          const year = dayjs(record.year.toString(), 'YYYY');

          form.setFieldValue('year', year);
          setEditIndex && setEditIndex(index);
        };

        return (
          <Space size={'middle'} className="text-lg ">
            {modules.includes(IModules.DETAIL) && (
              <Tooltip placement="top" title={'Xem chi tiết'}>
                <EyeIcon
                  className="cursor-pointer"
                  onClick={() =>
                    navigate(`${path.HO_FOLLOW_PLAN_DETAIL}`, {
                      state: record,
                    })
                  }
                />
              </Tooltip>
            )}
            {modules.includes(IModules.EDIT) && (
              <Tooltip placement="top" title={'Chỉnh sửa'}>
                <EditIcon className={classes} onClick={handleClickEdit} />
              </Tooltip>
            )}
          </Space>
        );
      };

      const operationCols: ColumnsTypeCustom = [
        {
          width: 100,
          title: 'Thao tác',
          fixed: 'right',
          align: 'center',
          render: (_, record, index) => {
            return editIndex === index ? (
              <Space size={'middle'} className="text-lg ">
                <ButtonAction tooltip="Lưu" onClick={() => handleSave(record)}>
                  <SaveTwoTone style={{ fontSize: 20 }} />
                </ButtonAction>
                <ButtonAction tooltip="Hủy" onClick={handleCancel}>
                  <CloseCircleTwoTone style={{ fontSize: 20 }} />
                </ButtonAction>
              </Space>
            ) : (
              renderActionButton(record, index)
            );
          },
        },
      ];

      const columns: ColumnsTypeCustom = [
        {
          width: 50,
          title: 'STT',
          type: 'number',
          className: 'align-top',
          render: (_, __, index) => index + 1 + page_size * (page - 1),
        },
        {
          width: 100,
          title: 'Năm',
          type: 'number',
          align: 'center',
          dataIndex: 'year',
          className: 'align-top',
          render(value, _, index) {
            const currentYear =
              typeof value === 'object' ||
              typeof value === 'string' ||
              typeof value === 'number'
                ? dayjs(value, 'YYYY').year().toString()
                : value.toString();

            const disabledDate = (currentDate: dayjs.Dayjs) => {
              return (
                dayjs(currentDate, 'YYYY').year() < new Date().getFullYear()
              );
            };

            return index === editIndex
              ? renderTable({
                  message: 'Nhập năm',
                  inputProps: {
                    name: 'year',
                  },
                  component: (
                    <DatePicker
                      picker="year"
                      inputReadOnly
                      value={currentYear}
                      disabledDate={disabledDate}
                    />
                  ),
                })
              : // : Number(currentYear);
                value;
          },
        },
        {
          width: 150,
          align: 'left',
          title: 'Tên chi nhánh',
          dataIndex: 'plant_name',
          className: 'align-top',
        },
        {
          width: 200,
          align: 'left',
          title: 'Loại doanh thu',
          dataIndex: 'type_revenue',
          className: 'align-top',
        },
        {
          type: 'number',
          currency: 'vi-VN',
          width: 200,
          title: 'Kế hoạch lũy kế',
          dataIndex: 'cumulative_plan',
          className: 'align-top',
          align: 'right',
          render: (value) =>
            value
              ? formatCurrency({
                  value,
                })
              : '-',
        },
        {
          type: 'number',
          currency: 'vi-VN',
          width: 200,
          title: 'Kế hoạch năm',
          dataIndex: 'year_plan',
          className: 'align-top',
          align: 'right',
          render: (value) =>
            value
              ? formatCurrency({
                  value,
                })
              : '-',
        },
        {
          type: 'number',
          currency: 'vi-VN',
          width: 200,
          title: 'Thực hiện',
          dataIndex: 'realized_revenue',
          className: 'align-top',
          align: 'right',
          render: (value) =>
            value
              ? formatCurrency({
                  value,
                })
              : '-',
        },
        {
          width: 200,
          align: 'right',
          title: '% đạt được lũy kế',
          dataIndex: 'cumulative_plan_percent',
          className: 'align-top',
        },
        {
          width: 200,
          align: 'right',
          title: '% đạt được theo năm',
          dataIndex: 'percentage_achieved_year',
          className: 'align-top',
        },
        {
          width: 100,
          title: 'Tiền tệ',
          isShowRender: true,
          align: 'left',
          dataIndex: 'unit_money',
          className: 'align-top',
        },
        {
          width: 150,
          title: 'Người cập nhật',
          dataIndex: 'modify_name',
          align: 'left',
          className: 'align-top',
        },
        {
          width: 210,
          title: 'Ngày cập nhật',
          dataIndex: 'modify_date',
          align: 'center',
          className: 'align-top',
          type: 'date',
          render: (value) =>
            dayjs(value).isValid()
              ? formatDate(value, 'DD/MM/YYYY HH:mm:ss')
              : value,
        },
        ...(modules.length ? operationCols : []),
      ];

      const getData = () => ({ headerRow: columns, datas: data });

      const checkFormValidate = async () => {
        try {
          await form.validateFields();
          return true;
        } catch (error) {
          return false;
        }
      };

      const handleSave = async (record: any) => {
        if (!(await checkFormValidate())) {
          form.validateFields();
          return;
        }

        const fields = form.getFieldsValue();
        fields['year'] = fields.year.year();

        const newValue =
          typeof editIndex !== 'undefined'
            ? { ...data.items[editIndex], ...fields }
            : fields;

        form.resetFields();
        setEditIndex(-1);
        onRowChange(record, newValue);
      };

      const handleCancel = () => {
        onCancel();
        form.resetFields();
      };

      return (
        <>
          <div className="text-end pb-2">
            <Text className="">
              Đơn vị: {data.items[0]?.unitMoney || 'triệu VND'}
            </Text>
          </div>
          <Form form={form}>
            <TableComponent
              scroll={{ x: 'max-content' }}
              rowKey={(record: any) => record.id}
              columns={columns}
              loading={loading}
              dataSource={data.items}
              pagination={
                pagination && {
                  current: data.pageInfo?.currentPage,
                  pageSize: data.pageInfo?.itemsPerPage,
                  total: data.pageInfo?.totalItems,
                  onChange: HOFollowCustomerPagination(),
                }
              }
            />
          </Form>
        </>
      );
    },
  ),
);

export default TableHOFollowPlan;
